import { makeStyles } from '@material-ui/styles'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import Text from 'core/elements/Text'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import { customValidator } from 'core/utils/fieldValidators'
import { routeManager } from 'core/utils/routes'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import React, { useCallback } from 'react'
import useReactRouter from 'use-react-router'
import SecurityGroupRulesParamFields from './SecurityGroupRulesParamFields'
import { createSecurityGroup, createSecurityGroupRules, deleteSecurityGroupRules } from './actions'
import { expandProtocolRules, getRuleJson } from './helpers'

const useStyles = makeStyles<Theme>((theme) => ({}))

interface Props {
  addRoute: Route
}

const nameValidator = customValidator((value) => {
  if (!value) {
    return true
  }
  return value !== 'default'
}, 'You may not create a securiy group named default. Please use another name.')

export default function CreateSecurityGroupModal({ addRoute }: Props) {
  const { history } = useReactRouter()
  const classes = useStyles()
  const defaultParams = {
    name: '',
    description: '',
    outboundSecurityGroupRules: [
      {
        cidr: '0.0.0.0/0',
        destination: 'cidr',
        portRange: 'all',
        securityGroup: null,
        type: 'allTraffic',
      },
    ],
    inboundSecurityGroupRules: [],
  }
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const {
    update: createSecurityGroupFn,
    updating: creatingSecurityGroup,
    error: creatingSecurityGroupError,
    reset,
  } = useUpdateAction(createSecurityGroup)
  const {
    update: rulesUpdate,
    updating: updatingRules,
    error: rulesError,
    reset: rulesReset,
  } = useUpdateAction(createSecurityGroupRules)
  const {
    update: deleteRules,
    updating: deletingRules,
    error: deleteRulesError,
    reset: deleteRulesReset,
  } = useUpdateAction(deleteSecurityGroupRules)

  const submitForm = useCallback(async () => {
    const body = {
      security_group: {
        name: params.name,
        description: params.description,
      },
    }
    const { success, response } = await createSecurityGroupFn({ body })
    if (success) {
      const securityGroupId = response?.id
      let allSuccess = true
      // Todo: If we allow editing of default security group rules, then
      // this will need to be updated too
      // Filter out outbound allTraffic, is considered a duplicate rule, and
      // will cause any other rules to not be applied
      const removeDefaultRules = !params.outboundSecurityGroupRules.some(
        (rule) => rule.type === 'allTraffic',
      )
      if (removeDefaultRules) {
        const rulesToRemove = response.security_group_rules
        const ruleIds = rulesToRemove.map((rule) => rule.id)
        const { success } = await deleteRules({ id: response.id, ruleIds })
        allSuccess = allSuccess && success
      }

      const outboundRuleJsons = params.outboundSecurityGroupRules
        .filter((rule) => rule.type !== 'allTraffic')
        .flatMap((rule) =>
          expandProtocolRules(rule, getRuleJson({ rule, direction: 'egress', securityGroupId })),
        )
      const inboundRuleJsons = params.inboundSecurityGroupRules.flatMap((rule) =>
        expandProtocolRules(rule, getRuleJson({ rule, direction: 'ingress', securityGroupId })),
      )

      // If there are no rules, then we don't need to create any
      if (outboundRuleJsons.length > 0 || inboundRuleJsons.length > 0) {
        const rulesBody = {
          security_group_rules: [...outboundRuleJsons, ...inboundRuleJsons],
        }
        const { success } = await rulesUpdate({
          id: securityGroupId,
          body: rulesBody,
        })
        allSuccess = allSuccess && success
      }

      if (allSuccess) {
        handleClose()
      }
    }
  }, [params])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    rulesReset()
    deleteRulesReset()
    history.push(routeManager.routes.openstack.securityGroups.path())
  }

  return (
    <ModalForm
      route={addRoute}
      // loading={loading}
      title={`Create Security Group`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={creatingSecurityGroup || updatingRules || deletingRules}
      error={creatingSecurityGroupError || rulesError || deleteRulesError}
      submitTitle={`Create Security Group`}
    >
      <>
        <ServiceUnhealthyInfo action="Security Group Creation" network />
        <FormFieldSection title="Basic Fields">
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            validations={[nameValidator]}
            required
          />
          <TextField
            id="description"
            label="Description"
            onChange={getParamsUpdater('description')}
            value={params.description}
          />
        </FormFieldSection>
        <FormFieldSection title="Outbound Security Group Rules">
          <Text variant="body2">
            <b>NOTE:</b> A rule allowing all outbound traffic is added by default. You can remove it
            if desired.
          </Text>
          <SecurityGroupRulesParamFields
            params={params}
            getParamsUpdater={getParamsUpdater}
            updateParams={updateParams}
            direction="outbound"
          />
        </FormFieldSection>
        <FormFieldSection title="Inbound Security Group Rules">
          <SecurityGroupRulesParamFields
            params={params}
            getParamsUpdater={getParamsUpdater}
            updateParams={updateParams}
            direction="inbound"
          />
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
